import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  PhonenumberPurchaseEditForm,
  PhonenumberPurchaseEditInitialValues,
  PhonenumberPurchaseComponents,
  PhonenumberPurchaseAdditionalParams,
  PhonenumberPurchaseFormOptions,
  onPhonenumberPurchaseFormSaved,
  IPhonenumberPurchaseFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedPhonenumberPurchase, ISharedPatchPhonenumberPurchase, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchPhonenumberPurchase, patchPhonenumberPurchase, getPhonenumberPurchaseUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedPhonenumberPurchase> {}

const PhonenumberPurchaseFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchPhonenumberPurchase(id).then(phonenumber_purchase => setValue(phonenumber_purchase));
  }, [id, setValue, ]);

  const param_props: IPhonenumberPurchaseFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && PhonenumberPurchaseEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && PhonenumberPurchaseFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchPhonenumberPurchase, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchPhonenumberPurchase) => value && save(() => patchPhonenumberPurchase(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onPhonenumberPurchaseFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.PhonenumberPurchase, value)}
    >
      <AtlasForm<ISharedPatchPhonenumberPurchase>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={PhonenumberPurchaseComponents}
        additionalParams={PhonenumberPurchaseAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={PhonenumberPurchaseEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('PHONENUMBER_PURCHASE')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PhonenumberPurchaseFormEditView = React.memo(withEdit(PhonenumberPurchaseFormEdit, getPhonenumberPurchaseUpdateSchema));
